<template>
    <div v-if="shouldRender" class="missing-translations-widget">
        <button
            type="button"
            class="missing-translations-widget__badge"
            :title="$t('§translation_editor.widget_tooltip')"
            @click="togglePanel"
        >
            <i class="pi pi-language" style="font-size: 1.3rem" />
            <span class="missing-translations-widget__count">{{ store.count }}</span>
        </button>

        <div v-if="panelOpen" class="missing-translations-widget__panel">
            <div class="missing-translations-widget__panel-header">
                <span>
                    {{ $t('§translation_editor.widget_header') }}
                    ({{ store.count }})
                </span>
                <button
                    type="button"
                    class="missing-translations-widget__close"
                    @click="panelOpen = false"
                >
                    <i class="pi pi-times" />
                </button>
            </div>

            <ul class="missing-translations-widget__list">
                <li
                    v-for="item in store.list"
                    :key="item.fullKey"
                    class="missing-translations-widget__item"
                >
                    <div class="missing-translations-widget__item-key" :title="item.fullKey">
                        {{ item.group }}.{{ item.key }}
                    </div>
                    <div class="missing-translations-widget__item-actions">
                        <button
                            type="button"
                            class="missing-translations-widget__action"
                            :title="$t('§translation_editor.action_edit')"
                            @click="handleEdit(item)"
                        >
                            <i class="pi pi-pencil" />
                        </button>
                        <button
                            type="button"
                            class="missing-translations-widget__action"
                            :title="$t('§translation_editor.action_open_manager')"
                            @click="handleOpenManager(item)"
                        >
                            <i class="pi pi-external-link" />
                        </button>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { useUserStore } from '@/stores/user'
import { useModalStore } from '@/stores/modal'
import { useMissingTranslationsStore } from '@/stores/missingTranslations'

export default {
    name: 'MissingTranslationsWidget',

    data() {
        return {
            userStore: useUserStore(),
            modalStore: useModalStore(),
            store: useMissingTranslationsStore(),
            panelOpen: false,
        }
    },

    computed: {
        canEdit() {
            return (
                this.userStore.isInternalUser &&
                this.userStore.can('view business settings')
            )
        },

        shouldRender() {
            return this.canEdit && this.store.count > 0
        },
    },

    watch: {
        shouldRender(value) {
            if (!value) this.panelOpen = false
        },
    },

    methods: {
        togglePanel() {
            this.panelOpen = !this.panelOpen
        },

        handleEdit(item) {
            this.panelOpen = false
            this.modalStore.setOpen({
                name: 'TranslationEditorModal',
                size: 'large',
                title: '§translation_editor.modal_title',
                data: {
                    group: item.group,
                    key: item.key,
                    fullKey: item.fullKey,
                },
            })
        },

        handleOpenManager(item) {
            const url = item.group ? `/translations/${item.group}` : '/translations'
            window.open(url, '_blank', 'noopener')
        },
    },
}
</script>

<style scoped>
.missing-translations-widget {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 1100;
}

.missing-translations-widget__badge {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 999px;
    border: 1px solid var(--color-warning, #d97706);
    background: var(--color-warning, #d97706);
    color: var(--text-color);
    cursor: pointer;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.missing-translations-widget__badge:hover {
    filter: brightness(0.95);
}

.missing-translations-widget__count {
    font-weight: 600;
    font-size: 1.4rem;
    margin-top: 3px;
}

.missing-translations-widget__panel {
    position: absolute;
    right: 0;
    bottom: 48px;
    width: 360px;
    max-height: 60vh;
    background: var(--color-surface, #fff);
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.missing-translations-widget__panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 12px;
    border-bottom: 1px solid var(--color-border, #e5e7eb);
    font-weight: 600;
}

.missing-translations-widget__close {
    background: none;
    border: none;
    cursor: pointer;
    color: var(--text-color, #111);
    padding: 4px;
}

.missing-translations-widget__list {
    list-style: none;
    margin: 0;
    padding: 0;
    overflow-y: auto;
}

.missing-translations-widget__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    border-bottom: 1px solid var(--color-border, #f1f1f1);
}

.missing-translations-widget__item:last-child {
    border-bottom: none;
}

.missing-translations-widget__item-key {
    font-family: monospace;
    font-size: 0.8125rem;
    color: var(--text-color, #111);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1 1 auto;
    min-width: 0;
}

.missing-translations-widget__item-actions {
    display: flex;
    gap: 4px;
    flex: 0 0 auto;
}

.missing-translations-widget__action {
    background: none;
    border: 1px solid var(--color-border, #e5e7eb);
    border-radius: 4px;
    cursor: pointer;
    padding: 4px 8px;
    color: var(--text-color, #111);
}

.missing-translations-widget__action:hover {
    background: var(--color-surface-hover, #f3f4f6);
}
</style>
